import React, { useEffect, useState } from 'react'
import { useRecipesHook } from '../../../Hooks/communicators/useRecipesHook';
import { useSelector } from 'react-redux';
import RecipeListComponent from './RecipeListComponent';

export default function ListRecipes() {
    const { fetchAllRecipes, searchRecipes, filterRecipes, recipeTypes, recipeTrimesters, selectableRecipeTags } = useRecipesHook();
    const { allRecipes } = useSelector((state: any) => state.recipe.value);
    const [searchQuery, setSearchQuery] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    
    // Filter states
    const [filterRecipeType, setFilterRecipeType] = useState('');
    const [filterRecipeTrimester, setFilterRecipeTrimester] = useState('');
    const [filterRecipeTags, setFilterRecipeTags] = useState<string[]>([]);
    const [filterIsPremium, setFilterIsPremium] = useState<boolean | undefined>(undefined);
    const [filterIsPriority, setFilterIsPriority] = useState<boolean | undefined>(undefined);
    const [filterIsDeleted, setFilterIsDeleted] = useState<boolean | undefined>(undefined);

    useEffect(() => {
        fetchAllRecipes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (searchQuery.trim() === '') {
            fetchAllRecipes();
        } else if (searchQuery.trim().length >= 3) {
            const delaySearch = setTimeout(() => {
                searchRecipes(searchQuery);
            }, 500); // 500ms debounce

            return () => clearTimeout(delaySearch);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery]);

    const handleApplyFilters = () => {
        const filters: any = {};
        
        if (filterRecipeType && filterRecipeType !== 'All') filters.recipeType = filterRecipeType;
        if (filterRecipeTrimester && filterRecipeTrimester !== 'All') filters.recipeTrimester = filterRecipeTrimester;
        if (filterRecipeTags.length > 0) filters.recipeTags = filterRecipeTags;
        if (filterIsPremium !== undefined) filters.isPremium = filterIsPremium;
        if (filterIsPriority !== undefined) filters.isPriority = filterIsPriority;
        if (filterIsDeleted !== undefined) filters.isDeleted = filterIsDeleted;

        filterRecipes(filters);
    };

    const handleClearFilters = () => {
        setFilterRecipeType('');
        setFilterRecipeTrimester('');
        setFilterRecipeTags([]);
        setFilterIsPremium(undefined);
        setFilterIsPriority(undefined);
        setFilterIsDeleted(undefined);
        fetchAllRecipes();
    };

    const handleTagToggle = (tag: string) => {
        if (filterRecipeTags.includes(tag)) {
            setFilterRecipeTags(filterRecipeTags.filter(t => t !== tag));
        } else {
            setFilterRecipeTags([...filterRecipeTags, tag]);
        }
    };

    return (
        <div className='flex justify-center items-center flex-col'>
            <h1 className='text-2xl font-bold'>All Recipes</h1>
            
            {/* Search Input */}
            <div className='w-full max-w-2xl my-4 px-4'>
                <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search recipes... (min. 3 characters)"
                    className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                />
                {searchQuery.trim().length > 0 && searchQuery.trim().length < 3 && (
                    <p className='text-sm text-gray-500 mt-1'>Please enter at least 3 characters to search</p>
                )}
            </div>

            {/* Filter Toggle Button */}
            <div className='w-full max-w-2xl px-4 mb-4'>
                <button
                    onClick={() => setShowFilters(!showFilters)}
                    className='px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2'
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
                    </svg>
                    {showFilters ? 'Hide Filters' : 'Show Filters'}
                </button>
            </div>

            {/* Filter Panel */}
            {showFilters && (
                <div className='w-full max-w-2xl px-4 mb-4'>
                    <div className='border border-gray-300 rounded-lg p-6 bg-gray-50'>
                        <h3 className='text-lg font-semibold mb-4'>Filter Recipes</h3>
                        
                        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                            {/* Recipe Type */}
                            <div>
                                <label className='block text-sm font-medium text-gray-700 mb-2'>Recipe Type</label>
                                <select
                                    value={filterRecipeType}
                                    onChange={(e) => setFilterRecipeType(e.target.value)}
                                    className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500'
                                >
                                    <option value="">All Types</option>
                                    {recipeTypes.filter(t => t.value !== 'All').map(type => (
                                        <option key={type.id} value={type.value}>{type.title}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Recipe Trimester */}
                            <div>
                                <label className='block text-sm font-medium text-gray-700 mb-2'>Trimester</label>
                                <select
                                    value={filterRecipeTrimester}
                                    onChange={(e) => setFilterRecipeTrimester(e.target.value)}
                                    className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500'
                                >
                                    <option value="">All Trimesters</option>
                                    {recipeTrimesters.filter(t => t.value !== 'All').map(trimester => (
                                        <option key={trimester.id} value={trimester.value}>{trimester.title}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Tags */}
                        <div className='mt-4'>
                            <label className='block text-sm font-medium text-gray-700 mb-2'>Tags</label>
                            <div className='flex flex-wrap gap-2'>
                                {selectableRecipeTags.map(tag => (
                                    <button
                                        key={tag.id}
                                        onClick={() => handleTagToggle(tag.value)}
                                        className={`px-3 py-1 rounded-full text-sm transition-colors ${
                                            filterRecipeTags.includes(tag.value)
                                                ? 'bg-blue-500 text-white'
                                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                        }`}
                                    >
                                        {tag.title}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Checkboxes */}
                        <div className='mt-4 grid grid-cols-1 md:grid-cols-3 gap-3'>
                            <label className='flex items-center gap-2 cursor-pointer'>
                                <input
                                    type='checkbox'
                                    checked={filterIsPremium === true}
                                    onChange={(e) => setFilterIsPremium(e.target.checked ? true : undefined)}
                                    className='w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500'
                                />
                                <span className='text-sm text-gray-700'>Premium Only</span>
                            </label>

                            <label className='flex items-center gap-2 cursor-pointer'>
                                <input
                                    type='checkbox'
                                    checked={filterIsPriority === true}
                                    onChange={(e) => setFilterIsPriority(e.target.checked ? true : undefined)}
                                    className='w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500'
                                />
                                <span className='text-sm text-gray-700'>Priority Only</span>
                            </label>

                            <label className='flex items-center gap-2 cursor-pointer'>
                                <input
                                    type='checkbox'
                                    checked={filterIsDeleted === true}
                                    onChange={(e) => setFilterIsDeleted(e.target.checked ? true : undefined)}
                                    className='w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500'
                                />
                                <span className='text-sm text-gray-700'>Deleted Only</span>
                            </label>
                        </div>

                        {/* Action Buttons */}
                        <div className='mt-6 flex gap-3'>
                            <button
                                onClick={handleApplyFilters}
                                className='px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium'
                            >
                                Apply Filters
                            </button>
                            <button
                                onClick={handleClearFilters}
                                className='px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors font-medium'
                            >
                                Clear Filters
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {
                allRecipes && allRecipes.map((recipe: any) => {
                    return (
                        <RecipeListComponent
                            mode='list'
                            _id={recipe._id}
                            recipeName={recipe.recipeName}
                            recipeType={recipe.recipeType}
                            recipeTrimester={recipe.recipeTrimester}
                            recipePreparationTime={recipe.recipePreparationTime}
                            recipeCookTime={recipe.recipeCookTime}
                            recipeServes={recipe.recipeServes}
                            recipeTags={recipe.recipeTags}
                            recipeMethod={recipe.recipeMethod}
                            recipeIngredients={recipe.recipeIngredients}
                            recipeImage={recipe.recipeImage}
                            recipeCarbs={recipe.recipeCarbs}
                            recipeProtein={recipe.recipeProtein}
                            recipeFats={recipe.recipeFats}
                            recipeCalories={recipe.recipeCalories}
                            recipeCalcium={recipe.recipeCalcium}
                            recipeVitaminA={recipe.recipeVitaminA}
                            recipeVitaminB6={recipe.recipeVitaminB6}
                            recipeVitaminB12={recipe.recipeVitaminB12}
                            recipeVitaminC={recipe.recipeVitaminC}
                            recipeVitaminD={recipe.recipeVitaminD}
                            recipeVitaminE={recipe.recipeVitaminE}
                            recipeVitaminK={recipe.recipeVitaminK}
                            recipeVitaminAUnit={recipe.recipeVitaminAUnit}
                            recipeVitaminB6Unit={recipe.recipeVitaminB6Unit}
                            recipeVitaminB12Unit={recipe.recipeVitaminB12Unit}
                            recipeVitaminCUnit={recipe.recipeVitaminCUnit}
                            recipeVitaminDUnit={recipe.recipeVitaminDUnit}
                            recipeVitaminEUnit={recipe.recipeVitaminEUnit}
                            recipeVitaminKUnit={recipe.recipeVitaminKUnit}
                            recipeCholesterol={recipe.recipeCholesterol}
                            recipeCholesterolUnit={recipe.recipeCholesterolUnit}
                            recipeCholine={recipe.recipeCholine}
                            recipeCholineUnit={recipe.recipeCholineUnit}
                            recipeFiber={recipe.recipeFiber}
                            recipeFiberUnit={recipe.recipeFiberUnit}
                            recipeFolicAcid={recipe.recipeFolicAcid}
                            recipeFolicAcidUnit={recipe.recipeFolicAcidUnit}
                            recipeIodine={recipe.recipeIodine}
                            recipeIodineUnit={recipe.recipeIodineUnit}
                            recipeIron={recipe.recipeIron}
                            recipeIronUnit={recipe.recipeIronUnit}
                            recipeMagnesium={recipe.recipeMagnesium}
                            recipeMagnesiumUnit={recipe.recipeMagnesiumUnit}
                            recipePotassium={recipe.recipePotassium}
                            recipePotassiumUnit={recipe.recipePotassiumUnit}
                            recipeSaturatedFat={recipe.recipeSaturatedFat}
                            recipeSaturatedFatUnit={recipe.recipeSaturatedFatUnit}
                            blogReferences={recipe.blogReferences}
                            createdAt={recipe.createdAt}
                            updatedAt={recipe.updatedAt}
                            isPremium={recipe.isPremium}
                            isDeleted={recipe.isDeleted}
                            isPriority={recipe.isPriority}
                            open={false}
                            setOpen={() => { }}
                        />
                    )
                })
            }
        </div>
    )
}
